import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import strings from 'strings';
import { activeNetworkSelector, userSelector } from 'redux/selectors';
import { useLazyGetFindemAuthQuery } from 'services/findemV2';
import { useLazyGetNetworkQuery } from 'services/organizationsV2';
import {
  createWidgetConfig,
  createDynamicFindemIframe,
  cleanupFindemWidget,
  FINDEM_PROFILE_DEFINITION_AGENT_ID,
} from 'helpers/findemWidget';

export const GROW_LIST_STEPS = [
  { id: 'profile-definition', label: 'Profile definition' },
  { id: 'outreach-setup', label: 'Outreach setup' },
  { id: 'launch', label: 'Launch' },
];

export const STEP_INDICES = {
  PROFILE_DEFINITION: 0,
  OUTREACH_SETUP: 1,
  LAUNCH: 2,
};

export const useGrowListAgent = ({ sharedList, onClose, campaign }) => {
  const [currentStep, setCurrentStep] = useState(STEP_INDICES.PROFILE_DEFINITION);
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const [widgetError, setWidgetError] = useState(null);
  const [campaignData, setCampaignData] = useState(null);

  const user = useSelector(userSelector);
  const activeNetwork = useSelector(activeNetworkSelector);
  const collectionId = activeNetwork?.id;

  const [getFindemAuth] = useLazyGetFindemAuthQuery();
  const [getNetwork] = useLazyGetNetworkQuery();

  const listName = sharedList?.name || 'this list';

  const signupFormUrl = `${window.location.origin}/${collectionId}/shared-lists/${sharedList?.slug}/signup`;

  const initializeWidget = useCallback(
    async (view) => {
      setIsWidgetLoading(true);
      setWidgetError(null);

      try {
        // Get auth token
        const { token } = await getFindemAuth(collectionId).unwrap();

        const network = await getNetwork(collectionId).unwrap();
        const sourcingCampaign = sharedList?.sourcingCampaign;

        let sessionId;
        if (sourcingCampaign?.id) {
          try {
            // Find session matching agent_id = FINDEM_PROFILE_DEFINITION_AGENT_ID
            sessionId = sourcingCampaign?.sessions?.find(
              (s) => s.agentId === FINDEM_PROFILE_DEFINITION_AGENT_ID,
            )?.sessionId;
          } catch (fetchError) {
            toast.error(fetchError?.data?.error ?? strings.genericError);
          }
        }

        // Create widget configuration
        const config = createWidgetConfig({
          job: null, // Not job-based for grow list
          user,
          token,
          collectionId,
          view,
          vcInfo: network?.description,
          vcUrl: network?.domain,
          jobBoardUrl: network?.url ?? '',
          agentId: null, // Will use default based on view
          signupFormUrl,
        });

        // Override config for grow list specific views
        const growListConfig = {
          ...config,
          sessionId,
          agentParams: {
            ...config.agentParams,
            shared_list_id: sharedList?.id?.toString() || '',
            shared_list_name: listName,
            is_grow_list: true,
          },
          sessionMetadata: {
            ...config.sessionMetadata,
            shared_list_id: sharedList?.id || null,
            list_id: sharedList?.id || null,
            sourcing_campaign_id: campaign?.id || null,
          },
        };

        // Load the Findem widget
        await createDynamicFindemIframe(growListConfig);
        setIsWidgetLoading(false);
      } catch (error) {
        setWidgetError(error);
        setIsWidgetLoading(false);
        toast.error(error?.data?.error ?? strings.genericError);
      }
    },
    [
      campaign?.id,
      collectionId,
      getFindemAuth,
      getNetwork,
      listName,
      sharedList?.id,
      sharedList?.sourcingCampaign,
      signupFormUrl,
      user,
    ],
  );

  const goToNextStep = useCallback(() => {
    setCurrentStep((prev) => {
      const nextStep = Math.min(prev + 1, STEP_INDICES.LAUNCH);
      return nextStep;
    });
  }, []);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, STEP_INDICES.PROFILE_DEFINITION));
  }, []);

  const handleClose = useCallback(() => {
    cleanupFindemWidget();

    onClose?.();
  }, [onClose]);

  const handleLaunch = useCallback(async () => {
    toast.success('Agent launched successfully!');
    handleClose();
  }, [handleClose]);

  const updateCampaignData = useCallback((data) => {
    setCampaignData((prev) => ({ ...prev, ...data }));
  }, []);

  useEffect(() => {
    if (currentStep === STEP_INDICES.PROFILE_DEFINITION) {
      initializeWidget('grow-list-profile');
    } else if (currentStep === STEP_INDICES.OUTREACH_SETUP) {
      initializeWidget('grow-list-outreach');
    }
  }, [currentStep, initializeWidget]);

  useEffect(
    () => () => {
      cleanupFindemWidget();
    },
    [],
  );

  return {
    currentStep,
    steps: GROW_LIST_STEPS,
    isWidgetLoading,
    widgetError,
    listName,
    campaignData,
    goToNextStep,
    goToPreviousStep,
    handleClose,
    handleLaunch,
    updateCampaignData,
    initializeWidget,
  };
};
